import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchACMById, fetchACMVersions, revertACMVersion, clearACMDetail } from '../../../Redux/Actions/actions';
import style from './ACMDetail.module.css';

const ACMDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { acmDetail, acmVersions, acmLoading } = useSelector(state => state);

  useEffect(() => {
    if (id) {
      dispatch(fetchACMById(id));
      dispatch(fetchACMVersions(id));
    }
    return () => { dispatch(clearACMDetail()); };
  }, [id, dispatch]);

  const handleRevert = async (versionId) => {
    if (!window.confirm('¿Revertir a esta versión? Se generará una nueva versión con los datos antiguos.')) return;
    try {
      await dispatch(revertACMVersion(id, versionId));
      dispatch(fetchACMVersions(id));
      alert('Versión revertida correctamente');
    } catch (e) {
      alert('Error al revertir: ' + (e.message || 'Error desconocido'));
    }
  };

  if (acmLoading && !acmDetail) return <div className={style.container}><p>Cargando...</p></div>;
  if (!acmDetail) return <div className={style.container}><p>ACM no encontrado.</p></div>;

  const foda = (() => {
    try {
      return typeof acmDetail.foda === 'string' ? JSON.parse(acmDetail.foda) : acmDetail.foda;
    } catch { return null; }
  })();

  return (
    <div className={style.container}>
      <div className={style.header}>
        <button className={style.backBtn} onClick={() => navigate('/acm-lista')}>← Volver</button>
        <h2>Detalle ACM</h2>
        <button className={style.editBtn} onClick={() => navigate(`/acm-editar/${id}`)}>
          Editar
        </button>
      </div>

      <div className={style.section}>
        <h3>Información General</h3>
        <div className={style.grid}>
          <div className={style.field}>
            <span className={style.label}>Propietario</span>
            <span className={style.value}>{acmDetail.propietario || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Ubicación</span>
            <span className={style.value}>{acmDetail.ubicacion || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Tipo de Inmueble</span>
            <span className={style.value}>{acmDetail.tipoInmueble || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Zona</span>
            <span className={style.value}>{acmDetail.zona || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Dimensiones</span>
            <span className={style.value}>{acmDetail.dimensiones || '-'} m²</span>
          </div>
          {acmDetail.cubierta && (
            <div className={style.field}>
              <span className={style.label}>Superficie Cubierta</span>
              <span className={style.value}>{acmDetail.cubierta} m²</span>
            </div>
          )}
          <div className={style.field}>
            <span className={style.label}>Antigüedad</span>
            <span className={style.value}>{acmDetail.antiguedad || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Estado de Conservación</span>
            <span className={style.value}>{acmDetail.estadoConservacion || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Servicios</span>
            <span className={style.value}>{acmDetail.servicios || '-'}</span>
          </div>
        </div>
      </div>

      <div className={style.section}>
        <h3>Valoración</h3>
        <div className={style.grid}>
          <div className={style.field}>
            <span className={style.label}>Valor M²</span>
            <span className={style.value}>U$D {acmDetail.valorM2 || '-'}</span>
          </div>
          <div className={style.field}>
            <span className={style.label}>Valor ACM</span>
            <span className={style.valueHighlight}>U$D {acmDetail.valorACM ? Number(acmDetail.valorACM).toLocaleString('es-ES') : '-'}</span>
          </div>
          {acmDetail.valorSugerido && (
            <div className={style.field}>
              <span className={style.label}>Valor Sugerido</span>
              <span className={style.valueHighlight}>U$D {Number(acmDetail.valorSugerido).toLocaleString('es-ES')}</span>
            </div>
          )}
        </div>
      </div>

      {foda && (
        <div className={style.section}>
          <h3>FODA</h3>
          <div className={style.fodaGrid}>
            <div className={style.fodaItem}>
              <strong>Fortalezas:</strong> {foda.fortalezas || '-'}
            </div>
            <div className={style.fodaItem}>
              <strong>Oportunidades:</strong> {foda.oportunidades || '-'}
            </div>
            <div className={style.fodaItem}>
              <strong>Debilidades:</strong> {foda.debilidades || '-'}
            </div>
            <div className={style.fodaItem}>
              <strong>Amenazas:</strong> {foda.amenazas || '-'}
            </div>
          </div>
        </div>
      )}

      <div className={style.section}>
        <h3>Versión Actual: {acmDetail.version || 1}</h3>
      </div>

      {/* Historial de versiones */}
      {acmVersions && acmVersions.length > 0 && (
        <div className={style.section}>
          <h3>Historial de Versiones</h3>
          <div className={style.timeline}>
            {acmVersions.map((version, idx) => (
              <div key={version.id || idx} className={style.timelineItem}>
                <div className={style.timelineDot} />
                <div className={style.timelineContent}>
                  <div className={style.timelineHeader}>
                    <strong>Versión {version.versionNumber || idx + 1}</strong>
                    <span className={style.timelineDate}>
                      {version.createdAt ? new Date(version.createdAt).toLocaleString('es-ES') : ''}
                    </span>
                  </div>
                  {version.motivo && (
                    <p className={style.timelineMotivo}>Motivo: {version.motivo}</p>
                  )}
                  <div className={style.timelineChanges}>
                    {version.propietario && <span>Propietario: {version.propietario}</span>}
                    {version.valorACM && <span>Valor ACM: U$D {Number(version.valorACM).toLocaleString('es-ES')}</span>}
                    {version.tipoInmueble && <span>Tipo: {version.tipoInmueble}</span>}
                  </div>
                  <button
                    className={style.revertBtn}
                    onClick={() => handleRevert(version.id)}
                    disabled={acmLoading}
                  >
                    Revertir a esta versión
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ACMDetail;
